/**
 * BulkUpload component - handles Excel template downloads and bulk product uploads
 */
import React, { useState, useRef } from 'react';
import { api } from '../../lib/store';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import { Upload, Download, FileSpreadsheet, BookOpen } from 'lucide-react';
import { logger } from '../../lib/logger';

export function BulkUpload() {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setResult(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await api.bulkUpload(formData);
      setResult(res.data);
      toast.success(res.data.message);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
      setResult({ error: err.response?.data?.detail || 'Upload failed' });
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const handleExportAll = async () => {
    try {
      toast.info('Exporting your entire catalog...');
      const response = await api.exportAllProducts();
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const today = new Date().toISOString().slice(0, 10);
      link.download = `HEDA_Product_Catalog_Export_${today}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('Product catalog exported! Edit it in Excel and re-upload to bulk-update.');
    } catch (err) {
      logger.error('Export error:', err);
      toast.error(err.response?.data?.detail || 'Failed to export catalog');
    }
  };

  const handleDownloadManual = async () => {
    try {
      const res = await api.downloadProductUploadManual();
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'HEDA_Plywoods_Product_Upload_Manual.pdf';
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Product Upload Manual downloaded');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to download manual');
    }
  };


  const handleDownloadTemplate = async (category = '') => {
    try {
      toast.info('Downloading template...');
      
      const response = await api.exportTemplate(category || undefined);
      
      // Check if backend returned HTML/JSON instead of xlsx (error case)
      const contentType = response.headers?.['content-type'] || '';
      if (!contentType.includes('spreadsheet') && !contentType.includes('xlsx')) {
        toast.error('Server did not return a valid Excel file');
        return;
      }

      const blob = new Blob([response.data], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      const safeName = category ? category.replace(/[/\s]+/g, '_') : 'all';
      link.download = `HEDA_${safeName}_Template.xlsx`;
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      window.URL.revokeObjectURL(downloadUrl);
      
      const pretty = category ? category.charAt(0).toUpperCase() + category.slice(1) : 'Sample (All Categories)';
      toast.success(`${pretty} template downloaded!`);
    } catch (err) {
      logger.error('Download error:', err);
      toast.error(err.response?.data?.detail || 'Failed to download template. Please try again.');
    }
  };

  // Template button configurations — grouped by product type
  const templates = [
    { name: 'Plywood', key: 'plywood' },
    { name: 'Blockboard', key: 'blockboard' },
    { name: 'MDF', key: 'mdf' },
    { name: 'WPC/PVC', key: 'wpc/pvc' },
    { name: 'ACP', key: 'acp' },
    { name: 'Doors', key: 'doors' },
    { name: 'Laminates', key: 'laminates' },
    { name: 'Highlighters', key: 'highlighters' },
    { name: 'Veneers', key: 'veneers' },
    { name: 'Wooden Flooring', key: 'wooden-flooring' },
    { name: 'Adhesives', key: 'adhesives' },
    { name: 'Hardware', key: 'hardware' },
    { name: 'Screws / Patti', key: 'screws-patti' },
  ];

  return (
    <div className="max-w-2xl" data-testid="bulk-upload-section">
      <Card className="rounded-2xl border-heda-line bg-white shadow-none">
        <CardContent className="p-5 space-y-4">
          <div className="text-center">
            <span className="mx-auto mb-3 flex h-16 w-16 items-center justify-center rounded-2xl bg-heda-olive-tint">
              <FileSpreadsheet className="w-8 h-8 text-heda-olive" />
            </span>
            <h2 className="font-display text-[19px] font-bold text-heda-ink mb-1">Bulk Upload Products</h2>
            <p className="text-sm text-heda-mute">Download category-specific templates and upload products</p>
            <Button
              onClick={handleDownloadManual}
              variant="outline"
              size="sm"
              className="mt-3 h-auto min-h-9 whitespace-normal rounded-full border-heda-line bg-white text-heda-ink shadow-none hover:bg-white hover:border-heda-gold hover:text-heda-gold"
              data-testid="product-upload-manual-btn"
            >
              <BookOpen className="w-4 h-4 mr-2" />
              Download Product Upload Manual (PDF)
            </Button>
          </div>

          {/* Template Downloads by Category */}
          <div className="p-4 bg-heda-cream rounded-xl border border-heda-line">
            <p className="heda-label mb-3">Download Templates</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {templates.map((t) => (
                <Button 
                  key={t.key}
                  onClick={() => handleDownloadTemplate(t.key)} 
                  variant="outline" 
                  size="sm"
                  className="rounded-full border-heda-line bg-white text-heda-ink shadow-none hover:bg-white hover:border-heda-gold hover:text-heda-gold h-9 justify-start min-w-0"
                  data-testid={`template-btn-${t.key}`}
                >
                  <Download className="w-3 h-3 mr-2 flex-shrink-0" /> {t.name}
                </Button>
              ))}
            </div>
            {/* Sample workbook */}
            <div className="mt-3 pt-3 border-t border-heda-line">
              <Button
                onClick={() => handleDownloadTemplate('')}
                variant="outline"
                size="sm"
                className="w-full h-auto min-h-9 whitespace-normal rounded-full border-heda-line bg-white text-heda-ink shadow-none hover:bg-white hover:border-heda-gold hover:text-heda-gold"
                data-testid="template-btn-sample"
              >
                <Download className="w-3 h-3 mr-1" /> Download Sample Workbook (all categories, pre-filled)
              </Button>
              <p className="text-[11px] text-heda-mute mt-1.5 text-center">
                Use this to see how every template looks with real data. Don't upload this directly — use the individual templates above.
              </p>
            </div>
          </div>

          {/* Template Info */}
          <div className="p-3 bg-heda-gold-tint/50 rounded-xl border border-heda-gold/30">
            <p className="text-[10.5px] font-semibold text-heda-gold uppercase tracking-wider mb-1">How templates work</p>
            <p className="text-xs text-heda-sub">
              Every template has two sheets: <strong>Data</strong> (with 2–3 example rows to copy) and <strong>Instructions</strong> (explains each column). Rate cells are rate per sqft for Plywood / Blockboard / MDF / WPC-PVC / ACP / Doors. Leave blank if a thickness isn't sold.
            </p>
          </div>

          {/* Export Current Catalog */}
          <div className="p-4 bg-heda-cream/50 rounded-xl border border-heda-line">
            <p className="heda-label mb-2">Export current catalog</p>
            <Button
              onClick={handleExportAll}
              className="w-full h-auto min-h-10 whitespace-normal rounded-full border-heda-line bg-white text-heda-ink shadow-none hover:bg-white hover:border-heda-gold hover:text-heda-gold"
              size="sm"
              data-testid="export-all-btn"
            >
              <Download className="w-4 h-4 mr-2" /> Download All Current Products (multi-sheet)
            </Button>
            <p className="text-[11px] text-heda-mute mt-2">
              Downloads every product in your database — one sheet per category (Plywood, Laminates, Doors, etc.). Edit rates/codes in Excel and re-upload the file below to bulk-update everything at once.
            </p>
          </div>
          
          {/* Update Info */}
          <div className="p-3 bg-emerald-50 rounded-xl border border-emerald-200">
            <p className="text-[10.5px] font-semibold text-emerald-700 uppercase tracking-wider mb-1">Smart Update</p>
            <p className="text-xs text-heda-sub">
              All templates include main_category column. Re-uploading will update existing products instead of creating duplicates.
            </p>
          </div>

          {/* Upload Button */}
          <div className="flex gap-2">
            <Button 
              onClick={() => fileRef.current?.click()} 
              disabled={uploading} 
              className="w-full h-12 rounded-2xl bg-heda-olive text-[15px] font-semibold text-white shadow-none hover:bg-heda-olive-dark" 
              data-testid="upload-excel-btn"
            >
              <Upload className="w-4 h-4 mr-2" /> {uploading ? 'Uploading...' : 'Upload Excel File'}
            </Button>
          </div>

          <input 
            ref={fileRef} 
            type="file" 
            accept=".xlsx,.xls" 
            onChange={handleUpload} 
            className="hidden" 
            data-testid="file-input" 
          />

          {result && (
            <div 
              className={`p-3 rounded-xl text-sm ${result.error ? 'bg-red-50 text-red-700' : 'bg-emerald-50 text-emerald-700'}`} 
              data-testid="upload-result"
            >
              {result.error || result.message}
              {(result.inserted !== undefined || result.updated !== undefined) && (
                <div className="mt-1 tabular-nums text-xs">
                  {result.inserted > 0 && <span className="text-emerald-700 font-bold">{result.inserted} new</span>}
                  {result.inserted > 0 && result.updated > 0 && <span>, </span>}
                  {result.updated > 0 && <span className="text-sky-700 font-bold">{result.updated} updated</span>}
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
